import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const HERO_IMG = "https://images.unsplash.com/photo-1561484930-998b6a7b22e8?auto=format&fit=crop&w=1400&q=80";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleOAuth() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.9 6.5 29.7 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.5-.2-3-.4-4.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34.9 6.5 29.7 4.5 24 4.5 16.3 4.5 9.6 8.6 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 45.5c5.5 0 10.5-2 14.3-5.4l-6.6-5.4c-2 1.5-4.6 2.4-7.7 2.4-5.3 0-9.8-3.4-11.4-8.1l-6.5 5C9.5 41.4 16.2 45.5 24 45.5z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.4C42.4 36.5 45 31 45 25c0-1.5-.2-3-.4-4.5z" />
    </svg>
  );
}

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0a0a0b]" data-testid="signup-page">
      <div className="hidden md:block relative overflow-hidden border-r border-white/5 bg-black">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0b]/40 via-transparent via-65% to-[#0a0a0b]" />
        <div className="absolute top-10 left-10">
          <Logo />
        </div>
        <div className="absolute bottom-12 left-10 right-10">
          <h2 className="font-display text-5xl text-white leading-[0.95] mb-3 tracking-tight">
            Step inside the <em className="silver-text not-italic">command surface</em>.
          </h2>
          <p className="font-body text-sm text-zinc-400 max-w-md">
            Provision your access in seconds. Sentinel calibrates to your operating cadence within
            minutes.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 relative">
        <div className="md:hidden absolute top-6 left-6">
          <Logo />
        </div>
        <div className="w-full max-w-md mt-12 md:mt-0">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            request access
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-white mb-2 tracking-tight">Provision.</h1>
          <p className="font-body text-sm text-zinc-400 mb-10">
            Already have access?{" "}
            <Link to="/login" className="text-white underline underline-offset-4 hover:text-[#dc2626]" data-testid="signup-to-login-link">
              Sign in
            </Link>
          </p>

          <button
            onClick={startGoogleOAuth}
            type="button"
            className="w-full flex items-center justify-center gap-3 btn-secondary-apex px-6 py-3.5 font-mono-apex text-[11px] uppercase tracking-[0.25em] mb-6"
            data-testid="google-oauth-btn"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <span className="flex-1 h-px bg-white/10" />
            <span className="font-mono-apex text-[9px] uppercase tracking-[0.3em] text-zinc-600">or with email</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Full Name
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-apex w-full px-4 py-3 font-body text-sm"
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Email
              </label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-apex w-full px-4 py-3 font-body text-sm"
                data-testid="signup-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Password <span className="lowercase tracking-normal text-zinc-600 normal-case">(min 6 chars)</span>
              </label>
              <input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="input-apex w-full px-4 py-3 font-body text-sm"
                data-testid="signup-password-input"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="font-mono-apex text-xs text-[#dc2626]" data-testid="signup-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary-apex px-6 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] disabled:opacity-60"
              data-testid="signup-submit-btn"
            >
              {loading ? "Provisioning…" : "Create access"}
            </button>
          </form>

          <div className="mt-10 font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-700">
            ← <Link to="/" className="hover:text-zinc-400">return to surface</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
